// src/b2b/pages/Paiement/index.jsx  (React Router v5)
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import StepNav from 'b2b/components/StepNav';
import QuoteSummary from 'b2b/components/QuoteSummary';
import countries from 'i18n-iso-countries';
import fr from 'i18n-iso-countries/langs/fr.json';
import PageLoader from 'b2b/components/PageLoader';
import {
  getAuthHeaders,
  getPaymentsBaseUrl,
  getBookingsBaseUrl,
} from '../../utils/bookingsBase';
import visa from 'assets/payments/visa-logo.png';
import applePay from 'assets/payments/apple_pay.png';
import googlePay from 'assets/payments/Google_Pay_Logo.svg.png';
import maestro from 'assets/payments/Maestro-logo.png';
import samsungPay from 'assets/payments/Samsung_Pay_Logo.svg';
import masterCard from 'assets/payments/Mastercard-logo.png';
/* ------------------------------ Helpers ------------------------------ */
const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);
const pad2 = (n) => String(n).padStart(2, '0');
const toISO = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;
countries.registerLocale(fr);
function useBookingFromStateOrApi() {
  const loc = useLocation();
  const { state = {} } = loc;
  const { bookingId: bookingIdFromUrl } = useParams(); // 👈 on lit bien :bookingId

  const [booking, setBooking] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // bookingId possible : state -> URL param -> query string
  const bookingId =
    state?.bookingId ||
    bookingIdFromUrl ||
    new URLSearchParams(loc.search).get('bookingId') ||
    null;

  useEffect(() => {
    // si on n'a vraiment aucun bookingId -> pas la peine de charger en boucle
    if (!bookingId) {
      setLoading(false);
      return;
    }

    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const base = getBookingsBaseUrl();
        const { data } = await axios.get(`${base}/${bookingId}`, {
          headers: getAuthHeaders(),
          timeout: 15000,
        });

        console.log('API /bookings response =', data.quoteLines);

        const normalized =
          data && data.booking && typeof data.booking === 'object' ? data.booking : data;

        if (mounted) setBooking(normalized);
      } catch (e) {
        console.error('LOAD_BOOKING_FAILED', e);
        if (mounted) setError('Impossible de charger la réservation.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [bookingId]);

  return { bookingId, booking, loading, error, state };
}

function sumLines(lines = [], kinds) {
  return lines
    .filter((l) => kinds.includes(l.kind))
    .reduce((acc, l) => acc + Number(l.total || 0), 0);
}

function buildExtrasFromQuoteLines(quoteLines = []) {
  if (!quoteLines.length) return null;

  const room = sumLines(quoteLines, ['room']);
  const tax = sumLines(quoteLines, ['tax']);
  const transfer = sumLines(quoteLines, ['transfer']);
  const flightBase = sumLines(quoteLines, ['flight_base']);

  // ✅ Si on a un flight_base, alors flight_delta ne doit plus compter
  const hasFlightBase = flightBase > 0;
  const flightDelta = hasFlightBase ? 0 : sumLines(quoteLines, ['flight_delta']);

  const board = sumLines(quoteLines, ['board']);
  const baggage = sumLines(quoteLines, ['baggage']);
  const other = sumLines(quoteLines, ['other', undefined, null].filter(Boolean));

  const grand = room + tax + transfer + flightBase + flightDelta + board + baggage + other;

  return {
    room,
    tax,
    transfer,
    flightBase,
    flightDelta,
    board,
    baggage,
    other,
    grand,
  };
}

/* ----------------------------- Sous-vues ----------------------------- */
function DOBSelect({
  value,
  onChange,
  minDate, // Date
  maxDate, // Date
}) {
  const y = String(value?.year ?? '');
  const m = String(value?.month ?? '');
  const d = String(value?.day ?? '');

  const minYear = minDate ? minDate.getFullYear() : 1900;
  const maxYear = maxDate ? maxDate.getFullYear() : new Date().getFullYear();

  const years = useMemo(() => range(minYear, maxYear).reverse(), [minYear, maxYear]);
  const months = range(1, 12);

  // ✅ jours dynamiques selon mois/année si dispo
  const daysInMonth = useMemo(() => {
    const yy = Number(y);
    const mm = Number(m);
    if (!yy || !mm) return 31;
    return new Date(yy, mm, 0).getDate(); // dernier jour du mois
  }, [y, m]);

  const days = useMemo(() => range(1, daysInMonth), [daysInMonth]);

  return (
    <div className="d-flex gap-2">
      <select
        className="form-control"
        value={d}
        onChange={(e) => onChange({ day: e.target.value, month: m, year: y })}
      >
        <option value="">Jour</option>
        {days.map((v) => (
          <option key={v} value={pad2(v)}>
            {pad2(v)}
          </option>
        ))}
      </select>

      <select
        className="form-control"
        value={m}
        onChange={(e) => onChange({ day: d, month: e.target.value, year: y })}
      >
        <option value="">Mois</option>
        {months.map((v) => (
          <option key={v} value={pad2(v)}>
            {pad2(v)}
          </option>
        ))}
      </select>

      <select
        className="form-control"
        value={y}
        onChange={(e) => onChange({ day: d, month: m, year: e.target.value })}
      >
        <option value="">Année</option>
        {years.map((yy) => (
          <option key={yy} value={String(yy)}>
            {yy}
          </option>
        ))}
      </select>
    </div>
  );
}

function addYears(date, years) {
  const d = new Date(date);
  d.setFullYear(d.getFullYear() + years);
  return d;
}

function buildTravelerErrors(travelers) {
  const errorsList = travelers.map(() => ({}));
  const seen = new Map(); // key -> first index

  travelers.forEach((t, i) => {
    const fn = norm(t?.firstName);
    const ln = norm(t?.lastName);

    // on contrôle seulement si les deux champs sont remplis
    if (!fn || !ln) return;

    const key = `${fn}__${ln}`;

    if (seen.has(key)) {
      const j = seen.get(key);

      // marquer les 2 cartes (celle existante + la nouvelle)
      errorsList[i].firstName = 'Ce voyageur est déjà saisi (doublon).';
      errorsList[i].lastName = 'Ce voyageur est déjà saisi (doublon).';

      errorsList[j].firstName = 'Ce voyageur est déjà saisi (doublon).';
      errorsList[j].lastName = 'Ce voyageur est déjà saisi (doublon).';
    } else {
      seen.set(key, i);
    }
  });

  return errorsList;
}

function TravelerCard({ index, kind, data, onChange, prefilledDOB, errors = {}, departDate }) {
  const title =
    kind === 'adult'
      ? `Adulte ${index + 1}`
      : kind === 'infant'
      ? `Bébé ${index + 1}`
      : `Enfant ${index + 1}`;

  const civ = data.civility || '';
  const fn = data.firstName || '';
  const ln = data.lastName || '';

  const hasDOB = (d) => d && (d.day || d.month || d.year);
  const dob = hasDOB(data.dob)
    ? data.dob
    : hasDOB(prefilledDOB)
    ? prefilledDOB
    : { day: '', month: '', year: '' };

  const base = departDate ? new Date(departDate) : new Date();

  // ✅ bornes par âge
  // Adult: au moins 13 ans -> dob <= base - 13 ans
  // Child: entre 2 et 11 ans inclus -> base-11y .. base-2y
  // Infant: < 2 ans -> base-2y .. base (aujourd'hui)
  const { minDate, maxDate } = useMemo(() => {
    if (kind === 'adult') {
      return {
        minDate: addYears(base, -120),
        maxDate: addYears(base, -13),
      };
    }
    if (kind === 'child') {
      return {
        minDate: addYears(base, -11),
        maxDate: addYears(base, -2),
      };
    }
    // infant
    return {
      minDate: addYears(base, -2),
      maxDate: base,
    };
  }, [kind, departDate]);

  return (
    <div className="card mb-3">
      <div className="card-header fw-semibold">{title}</div>
      <div className="card-body">
        <div className="row g-3">
          <div className="col-12 col-md-3">
            <label className="form-label">Civilité</label>
            <select
              className={`form-control ${errors.civility ? 'is-invalid' : ''}`}
              value={civ}
              onChange={(e) => onChange({ ...data, civility: e.target.value })}
            >
              <option value="">—</option>
              <option value="Mr">M.</option>
              <option value="Mme">Mme</option>
              <option value="Mlle">Mlle</option>
            </select>
            {errors.civility && <div className="invalid-feedback">{errors.civility}</div>}
          </div>

          <div className="col-12 col-md-4">
            <label className="form-label">Prénom</label>
            <input
              className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
              value={fn}
              onChange={(e) => onChange({ ...data, firstName: e.target.value })}
            />
            {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
          </div>

          <div className="col-12 col-md-5">
            <label className="form-label">Nom</label>
            <input
              className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
              value={ln}
              onChange={(e) => onChange({ ...data, lastName: e.target.value })}
            />
            {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
          </div>

          <div className="col-12">
            <label className="form-label">
              Date de naissance{' '}
              {kind === 'adult' ? (
                <span className="text-danger">*</span>
              ) : (
                <span className="text-muted">(facultatif)</span>
              )}
            </label>

            <DOBSelect
              value={dob}
              onChange={(v) => onChange({ ...data, dob: v })}
              minDate={minDate}
              maxDate={maxDate}
            />

            {/* ✅ on n’affiche pas d’erreur si vide */}
            {errors.dob && hasDOB(dob) && (
              <div className="invalid-feedback d-block">{errors.dob}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ Page ------------------------------ */
export default function Paiement() {
  const history = useHistory();
  const { bookingId, booking, loading, error, state } = useBookingFromStateOrApi();
  const [saving, setSaving] = useState(false);
  const [payError, setPayError] = useState(null);
  const [selectedPayMode, setSelectedPayMode] = useState('card'); // 'card' | 'account'
  const [selectedCardOption, setSelectedCardOption] = useState('full'); // 'deposit40' | 'full'
  const [acceptCgv, setAcceptCgv] = useState(false);
  const [cgvError, setCgvError] = useState('');

  const bookingChildrenDOB = useMemo(() => booking?.childrenDOB || booking?.chidrenDOB || [], [
    booking?.childrenDOB,
    booking?.chidrenDOB,
  ]);

  const bookingTravelers = useMemo(() => booking?.travelers || [], [booking]);

  const bookingAdults = useMemo(() => bookingTravelers.filter((t) => t.kind !== 'child'), [
    bookingTravelers,
  ]);
  const bookingChildren = useMemo(() => bookingTravelers.filter((t) => t.kind === 'child'), [
    bookingTravelers,
  ]);

  const bookingInfants = useMemo(() => bookingTravelers.filter((t) => t.kind === 'infant'), [
    bookingTravelers,
  ]);

  // venant du state (quand on arrive depuis TripSelection)
  const selectedRoom = state?.selectedRoom || booking?.selectedRoom || null;

  const selectedFlight = state?.selectedFlights || booking?.selectedFlights || null;

  const roomCategories = state?.roomCategories || booking?.selectedRoom?.items || [];

  const selectedMealPlan =
    state?.selectedMealPlan ||
    (booking?.selectedRoom
      ? {
          desc: booking.selectedRoom.boardLabel,
          total: booking.selectedRoom.boardTotal,
          currency: booking.selectedRoom.currency || 'EUR',
          included: false,
        }
      : null);

  const num = (v) => (v == null ? 0 : Number(v) || 0);
  // Loader "enregistrement"
  const countryOptions = useMemo(() => {
    const map = countries.getNames('fr', { select: 'official' }); // { FR: "France", ... }
    return Object.entries(map)
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  }, []);
  // contact
  // const [contact, setContact] = useState({
  //   civility: '',
  //   firstName: '',
  //   lastName: '',
  //   phone: '',
  //   phone2: '',
  //   email: '',
  //   address: '',
  //   zip: '',
  //   city: '',
  //   countryCode: 'FR',
  //   travelerIsPayer: true,
  // });

  // function splitAddress(full) {
  //   if (!full || typeof full !== 'string')
  //     return {
  //       address: '',
  //       zip: '',
  //       city: '',
  //     };

  //   // Ex : "12 rue du Louvre 75001 Paris"
  //   const parts = full.trim().split(/\s+/);

  //   // Trouver le premier élément numérique à 4 ou 5 chiffres
  //   const zipIndex = parts.findIndex((p) => /^[0-9]{4,5}$/.test(p));

  //   if (zipIndex === -1) {
  //     return {
  //       address: full,
  //       zip: '',
  //       city: '',
  //     };
  //   }

  //   const addressPart = parts.slice(0, zipIndex).join(' ');
  //   const zipPart = parts[zipIndex];
  //   const cityPart = parts.slice(zipIndex + 1).join(' ');

  //   return {
  //     address: addressPart || '',
  //     zip: zipPart || '',
  //     city: cityPart || '',
  //   };
  // }

  const [contact, setContact] = useState(() => {
    let profile = null;

    try {
      const raw = localStorage.getItem('userProfil');
      profile = raw ? JSON.parse(raw) : null;
    } catch (e) {
      console.warn('Invalid userProfil in localStorage', e);
    }

    // const split = splitAddress(profile?.adresse);

    return {
      civility: profile?.civility || '',
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      companyName: profile?.companyName || '',
      phone: profile?.phone || '',
      phone2: profile?.phone2 || '',
      email: profile?.email || '',
      address: profile?.address.street || '',
      zip: profile?.address.postalCode || '',
      city: profile?.address.city || '',
      countryCode: profile?.countryCode || 'FR',
      travelerIsPayer: true,
      paymentMode: profile?.paymentMode,
    };
  });
  console.log(
    '%csrc\b2bPaiementindex.jsx:426 contact',
    'color: #007acc;',
    contact.paymentMode,
  );
  const tokenB2B = localStorage.getItem('token_b2b');
  const storedProfile = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userProfil') || 'null');
    } catch {
      return null;
    }
  }, []);

  const isB2BUser = !!tokenB2B;
  const hasStoredProfile =
    !!storedProfile &&
    (storedProfile.email || storedProfile.firstName || storedProfile.lastName);

  const lockFields = isB2BUser && hasStoredProfile;
  const [errors, setErrors] = useState({});
  const [travelerErrors, setTravelerErrors] = useState({});

  useEffect(() => {
    if (!booking || !booking.contact) return;

    const c = booking.contact || {};
    // const split = splitAddress(c.address);
    setContact((prev) => ({
      ...prev,
      civility: c.civility || prev.civility || '',
      firstName: c.firstName || prev.firstName || '',
      lastName: c.lastName || prev.lastName || '',
      phone: c.phone || prev.phone || '',
      phone2: c.phone2 || prev.phone2 || '',
      email: c.email || prev.email || '',
      address: c.address.street || prev.address,
      zip: c.address.postalCode || prev.zip,
      city: c.address.city || prev.city,
      countryCode: c.countryCode || prev.countryCode || 'FR',
      travelerIsPayer: c.travelerIsPayer ?? prev.travelerIsPayer ?? true,
    }));
  }, [booking]);

  // voyageurs (formulaires dynamiques)
  const [adults, setAdults] = useState([]);
  const [children, setChildren] = useState([]);
  const [infants, setInfants] = useState([]);

  useEffect(() => {
    if (!isB2BUser) return;
    if (!storedProfile) return;

    setContact((prev) => {
      const next = {
        ...prev,
        firstName: prev.firstName || storedProfile.firstName || '',
        lastName: prev.lastName || storedProfile.lastName || '',
        companyName: prev.companyName || storedProfile.companyName || '',
        civility: prev.civility || storedProfile.civility || '',
        email: prev.email || storedProfile.email || '',
        phone: prev.phone || storedProfile.phone || '',
      };

      // ✅ évite setState si identique (évite rerenders inutiles)
      const same =
        next.firstName === prev.firstName &&
        next.lastName === prev.lastName &&
        next.companyName === prev.companyName &&
        next.civility === prev.civility &&
        next.email === prev.email &&
        next.phone === prev.phone;

      return same ? prev : next;
    });
  }, [isB2BUser, storedProfile]);

  // Nombre attendu d’adultes / enfants : booking en priorité, sinon state.search

  const numAdults = Number(booking?.adults ?? bookingAdults.length ?? 0);
  const numChildren = Number(booking?.children ?? bookingChildren.length ?? 0);
  const numInfants = Number(booking?.infants ?? bookingInfants.length ?? 0);
  // si tu stockes les DOB bébé côté booking : booking.infantsDOB
  const bookingInfantsDOB = useMemo(() => booking?.infantsDOB || [], [booking]);

  function travelerToForm(t, kindDefault) {
    if (!t) return {};
    const d = t.dob ? new Date(t.dob) : null;

    return {
      kind: t.kind || kindDefault,
      civility: t.civility || '',
      firstName: t.firstName || '',
      lastName: t.lastName || '',
      dob: d
        ? {
            day: pad2(d.getDate()),
            month: pad2(d.getMonth() + 1),
            year: String(d.getFullYear()),
          }
        : null,
    };
  }

  const norm = (s = '') => String(s).trim().replace(/\s+/g, ' ').toLowerCase();

  function pushErr(errors, key, field, msg) {
    if (!errors[key]) errors[key] = {};
    errors[key][field] = msg;
  }

  function validateTravelers({ adults = [], children = [], infants = [], departDate }) {
    const errors = {};

    const base = departDate ? new Date(departDate) : new Date();

    const addYears = (date, years) => {
      const d = new Date(date);
      d.setFullYear(d.getFullYear() + years);
      return d;
    };

    const parseDob = (dob) => {
      const y = Number(dob?.year);
      const m = Number(dob?.month);
      const d = Number(dob?.day);
      if (!y || !m || !d) return null;

      const dt = new Date(y, m - 1, d);
      // check date réelle (évite 31/02)
      if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) {
        return null;
      }
      return dt;
    };

    const boundsByKind = (kind) => {
      if (kind === 'adult') return { min: addYears(base, -120), max: addYears(base, -13) };
      if (kind === 'child') return { min: addYears(base, -11), max: addYears(base, -2) };
      // infant
      return { min: addYears(base, -2), max: base };
    };

    const check = (arr, kind) => {
      const { min, max } = boundsByKind(kind);

      arr.forEach((t, i) => {
        const key = `${kind}-${i}`;
        const e = {};

        if (!t?.civility) e.civility = 'Merci de choisir une civilité.';
        if (!t?.firstName?.trim()) e.firstName = 'Le prénom est obligatoire.';
        if (!t?.lastName?.trim()) e.lastName = 'Le nom est obligatoire.';

        const d = t?.dob || {};
        const hasAnyDob = !!(d.day || d.month || d.year);
        const hasFullDob = !!(d.day && d.month && d.year);

        // 1) ADULTE: obligatoire
        if (kind === 'adult' && !hasFullDob) {
          e.dob = 'La date de naissance est obligatoire pour les adultes.';
        }

        // 2) CHILD/INFANT: facultatif mais si commencé -> complet
        if (kind !== 'adult' && hasAnyDob && !hasFullDob) {
          e.dob = 'Merci de compléter Jour / Mois / Année.';
        }

        // 3) Si DOB complète -> valider date réelle + bornes d'âge
        if (hasFullDob) {
          const dt = parseDob(d);

          if (!dt) {
            e.dob = 'Date de naissance invalide.';
          } else if (dt < min || dt > max) {
            if (kind === 'adult') e.dob = 'L’âge doit être ≥ 13 ans.';
            if (kind === 'child') e.dob = 'L’âge doit être entre 2 et 11 ans.';
            if (kind === 'infant') e.dob = 'L’âge doit être < 2 ans.';
          }
        }

        if (Object.keys(e).length) errors[key] = e;
      });
    };

    // ✅ 1) Validations individuelles
    check(adults, 'adult');
    check(children, 'child');
    check(infants, 'infant');

    // ✅ 2) Validation anti-doublon prénom+nom (tous voyageurs)
    const norm = (s = '') => String(s).trim().replace(/\s+/g, ' ').toLowerCase();

    const pushErr = (key, field, msg) => {
      if (!errors[key]) errors[key] = {};
      // ne pas écraser un message plus important déjà présent (optionnel)
      if (!errors[key][field]) errors[key][field] = msg;
    };

    const all = [
      ...adults.map((t, i) => ({ key: `adult-${i}`, t })),
      ...children.map((t, i) => ({ key: `child-${i}`, t })),
      ...infants.map((t, i) => ({ key: `infant-${i}`, t })),
    ];

    const seen = new Map(); // "fn__ln" -> key

    for (const { key, t } of all) {
      const fn = norm(t?.firstName);
      const ln = norm(t?.lastName);

      // Check seulement si les deux champs sont remplis
      if (!fn || !ln) continue;

      const k = `${fn}__${ln}`;

      if (seen.has(k)) {
        const otherKey = seen.get(k);
        const msg = 'Nom et prénom déjà utilisés pour un autre voyageur.';

        // Marquer les 2 voyageurs
        pushErr(key, 'firstName', msg);
        pushErr(key, 'lastName', msg);

        pushErr(otherKey, 'firstName', msg);
        pushErr(otherKey, 'lastName', msg);
      } else {
        seen.set(k, key);
      }
    }

    return errors;
  }

  // (ré)initialise le bon nombre de cartes adultes
  // (ré)initialise les cartes adultes
  useEffect(() => {
    setAdults((prev) =>
      Array.from({ length: numAdults }, (_, i) => {
        // si l’utilisateur est déjà en train de saisir, on ne touche pas
        if (prev[i] && (prev[i].firstName || prev[i].lastName || prev[i].dob)) {
          return prev[i];
        }
        // sinon on pré-remplit depuis booking.travelers
        return travelerToForm(bookingAdults[i], 'adult');
      }),
    );
  }, [numAdults, bookingAdults]);

  // (ré)initialise les cartes enfants (+ DOB depuis childrenDOB si besoin)
  useEffect(() => {
    setChildren((prev) => {
      const arr = Array.from({ length: numChildren }, (_, i) => prev[i] || {});

      return arr.map((item, i) => {
        // si l’utilisateur a déjà commencé à remplir, on garde
        if (item.firstName || item.lastName || item.dob) return item;

        const src = bookingChildren[i];
        const fromTraveler = travelerToForm(src, 'child');

        // DOB prioritaire depuis traveler, sinon depuis childrenDOB
        let dob = fromTraveler.dob;
        const pre = bookingChildrenDOB[i];
        if (pre?.day && pre?.month && pre?.year && !dob) {
          dob = {
            day: pad2(pre.day),
            month: pad2(pre.month),
            year: String(pre.year),
          };
        }

        return { ...item, ...fromTraveler, dob };
      });
    });
  }, [numChildren, bookingChildren, bookingChildrenDOB]);

  useEffect(() => {
    setInfants((prev) => {
      const arr = Array.from({ length: numInfants }, (_, i) => prev[i] || {});

      return arr.map((item, i) => {
        // si l’utilisateur a déjà commencé à remplir, on garde
        if (item.firstName || item.lastName || item.dob) return item;

        const src = bookingInfants[i];
        const fromTraveler = travelerToForm(src, 'infant');

        // DOB prioritaire depuis traveler, sinon depuis infantsDOB
        let dob = fromTraveler.dob;
        const pre = bookingInfantsDOB[i];

        if (pre?.day && pre?.month && pre?.year && !dob) {
          dob = {
            day: pad2(pre.day),
            month: pad2(pre.month),
            year: String(pre.year),
          };
        }

        return { ...item, ...fromTraveler, dob };
      });
    });
  }, [numInfants, bookingInfants, bookingInfantsDOB]);

  const [b2bDiscount] = useState(() => {
    try {
      const raw = localStorage.getItem('userProfil');

      if (!raw) return 0;

      const user = JSON.parse(raw);
      const d = Number(user.discount || 0);

      return Number.isFinite(d) ? d : 0;
    } catch {
      return 0;
    }
  });

  const quoteExtrasFromBooking = useMemo(() => {
    if (!booking?.quoteLines || !booking.quoteLines.length) return null;
    return buildExtrasFromQuoteLines(booking.quoteLines);
  }, [booking?.quoteLines]);

  // Données pour le résumé à droite
  const quoteProduct = useMemo(() => {
    // 1) Cas classique : on vient de /selection, on a tout dans state
    if (state?.product) return state.product;

    // 2) Cas retour Worldline (annulé / échec / succès) : on reconstruit depuis booking
    if (!booking) {
      return {
        hotelName: 'Séjour',
        city: '',
        imageUrl: null,
      };
    }

    return {
      hotelName: booking.productName || 'Séjour',
      city: booking.destination?.city || '',
      country: booking.destination?.country || '',
      imageUrl: booking.productImage || null,
    };
  }, [state?.product, booking]);

  const quoteSearch = useMemo(() => {
    if (state?.search) return state.search;
    if (!booking) return {};

    const startDate = booking.stayStartDate || booking.date || null;

    // si endDate manque, on la reconstruit à partir de la durée
    let endDate = booking.stayEndDate || booking.endDate || null;

    if (!endDate && startDate && booking.durationNights) {
      const start = new Date(startDate);
      endDate = new Date(start.getTime() + Number(booking.durationNights) * 86400000);
    }

    return {
      startDate,
      endDate,
      nights: booking.durationNights || 0, // ✅ utile pour QuoteSummary
      departCity: booking.fromLabel || booking.from || '',
      origin: booking.from || '', // ✅ si QuoteSummary utilise origin/fromCode
      fromCode: booking.from || '', // ✅
      adults: booking.adults || 0,
      children: booking.children || 0,
      infants: booking.infants || 0, // ✅ si tu l’affiches
      productCode: booking.productCode || null, // ✅ parfois nécessaire
    };
  }, [state?.search, booking]);

  const selectedPrestationsForSummary = useMemo(() => {
    const fromState = Array.isArray(state?.selectedPrestations)
      ? state.selectedPrestations
      : [];

    const fromBooking = Array.isArray(booking?.prestations) ? booking.prestations : [];

    const source = fromState.length ? fromState : fromBooking;

    return source.map((p, idx) => {
      const qty = Math.max(1, Number(p.qty || p.quantity || 1));
      const unitPrice = Number(p.unitPrice || p.unit || 0);
      const total = p.total != null ? Number(p.total) : Number((unitPrice * qty).toFixed(2));

      return {
        groupId: p.groupId || null,
        groupLabel: p.groupLabel || null,
        optionId: p.optionId || null,
        code: p.code || `ADDON_${idx}`,
        label: p.label || 'Prestation',
        qty,
        unitPrice,
        total,
        currency: p.currency || booking?.currency || quotePrice?.currency || 'EUR',
        priceRef: p.priceRef || null,
        quantityRule: p.quantityRule || null,
      };
    });
  }, [
    state?.selectedPrestations,
    booking?.prestations,
    booking?.currency,
    quotePrice?.currency,
  ]);

  const prestationsTotalForSummary = React.useMemo(
    () => selectedPrestationsForSummary.reduce((sum, p) => sum + (Number(p.total) || 0), 0),
    [selectedPrestationsForSummary],
  );

  const isB2B = !!localStorage.getItem('token_b2b'); // B2B si token
  const isB2C = !isB2B;

  // Date de séjour (startDate)
  const stayStartDateStr =
    quoteSearch?.startDate || booking?.stayStartDate || booking?.date || null;

  const daysUntilStay = useMemo(() => {
    if (!stayStartDateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const stay = new Date(stayStartDateStr);
    stay.setHours(0, 0, 0, 0);

    const diffMs = stay.getTime() - today.getTime();
    return Math.floor(diffMs / 86400000); // jours
  }, [stayStartDateStr]);

  const canShowDeposit40 = useMemo(() => {
    // règle: diff >= 45 jours
    if (daysUntilStay == null) return false;
    return daysUntilStay >= 45;
  }, [daysUntilStay]);

  // "En compte" autorisé seulement en B2B + admin a activé (paymentMode === 'account')
  const canShowAccount = useMemo(() => {
    const allowed = String(contact?.paymentMode || 'card').toLowerCase() === 'account';
    return isB2B && allowed;
  }, [isB2B, contact?.paymentMode]);

  // CB est toujours dispo (B2B et B2C)
  const canShowCard = true;

  useEffect(() => {
    if (!canShowDeposit40 && selectedCardOption === 'deposit40') {
      setSelectedCardOption('full');
    }
  }, [canShowDeposit40, selectedCardOption]);

  useEffect(() => {
    if (!canShowAccount && selectedPayMode === 'account') {
      setSelectedPayMode('card');
    }
  }, [canShowAccount, selectedPayMode]);

  const mapKindToRole = (k) => {
    const kind = String(k || '').toLowerCase();
    if (kind === 'tax') return 'Tax';
    if (kind === 'transfer') return 'Supplement';
    if (kind === 'flight_base' || kind === 'flight_delta') return 'Transportation';
    if (kind === 'room') return 'Base';
    if (kind === 'board') return 'Supplement';
    if (kind === 'baggage') return 'Supplement';
    return 'Supplement';
  };

  const normalizeQuoteLinesForSummary = (lines = []) =>
    lines.map((l, idx) => ({
      role: mapKindToRole(l.kind),
      code: l.code || `LINE_${idx + 1}`,
      ref: l.code || `LINE_${idx + 1}`,
      description: l.label || l.desc || `Ligne ${idx + 1}`,
      quantity: l.qty ?? null,
      unit: l.unit ?? null,
      total: Number(l.total || 0),
      currency: l.currency || 'EUR',
    }));

  const quotePrice = useMemo(() => {
    if (state?.priceSeed) return state.priceSeed;

    if (!booking) {
      return { stay: 0, fees: 0, taxes: 0, currency: 'EUR', items: [] };
    }

    return {
      stay: booking.finalTotalPrice || booking.totalPrice || 0,
      fees: 0,
      taxes: 0, // on laisse QuoteSummary recalculer via items
      currency: booking.currency || 'EUR',
      items: normalizeQuoteLinesForSummary(booking.quoteLines || []),
    };
  }, [state?.priceSeed, booking]);

  // const quoteSums = useMemo(() => {
  //   const lines = booking?.quoteLines || [];

  //   const sum = (kind) =>
  //     lines.filter((l) => l.kind === kind).reduce((acc, l) => acc + (Number(l.total) || 0), 0);

  //   return {
  //     room: sum('room'),
  //     tax: sum('tax'),
  //     transfer: sum('transfer'),
  //     flightBase: sum('flight_base'),
  //     flightDelta: sum('flight_delta'),
  //     baggage: sum('baggage'),
  //     board: sum('board'),
  //     other: sum('other'),
  //   };
  // }, [booking?.quoteLines]);

  // ⚠️ si on vient de TripSelection, on a extras détaillé
  const quoteExtras = useMemo(() => {
    // 1) Cas classique : on vient de /selection
    if (state?.priceSeed && selectedRoom && selectedFlight) {
      const roomExtra = num(selectedRoom?.supplement);

      const flightsTotal =
        num(selectedFlight.transportDelta ?? selectedFlight.transportTotal ?? 0) +
        num(selectedFlight.luggagesTotal);

      const grand =
        state.computedTotal ??
        num(quotePrice.stay) +
          num(quotePrice.fees) +
          num(quotePrice.taxes) +
          roomExtra +
          flightsTotal;

      return {
        room: roomExtra,
        flights: flightsTotal,
        transportDelta: selectedFlight.transportDelta ?? selectedFlight.transportTotal ?? 0,
        luggagesTotal: selectedFlight.luggagesTotal || 0,
        luggagesSelection: selectedFlight.luggagesSelection || null,
        grand,
        categories: roomCategories,
        mealPlan: selectedMealPlan
          ? {
              desc: selectedMealPlan.desc,
              total: selectedMealPlan.total,
              currency: selectedMealPlan.currency || 'EUR',
              included: !!selectedMealPlan.included,
            }
          : null,
      };
    }

    // 2) Cas retour Worldline (annulé / échoué / succès) :
    //    on reconstruit les mêmes extras à partir du booking
    if (booking?.selectedRoom && booking?.selectedFlights) {
      const sr = booking.selectedRoom;
      const sf = booking.selectedFlights;

      const roomExtra = num(sr.supplement);

      const flightsTotal =
        num(sf.deltaPrice ?? sf.transportTotal ?? 0) + num(sf.luggagesTotal);

      const grand = num(booking.finalTotalPrice ?? booking.totalPrice ?? 0);
      console.log('%csrc\b2bPaiementindex.jsx:641 grand', 'color: #007acc;', grand);
      return {
        room: roomExtra,
        flights: flightsTotal,
        transportDelta: sf.deltaPrice ?? sf.transportTotal ?? 0,
        luggagesTotal: sf.luggagesTotal || 0,
        luggagesSelection: sf.luggagesSelection || null,
        grand,
        categories: sr.items || [], // => labels "Chambre 1", ...
        mealPlan: sr.boardTotal
          ? {
              desc: sr.boardLabel, // => "Supplément pension complète"
              total: sr.boardTotal,
              currency: sr.currency || booking.currency || 'EUR',
              included: false,
            }
          : null,
      };
    }

    // 3) Fallback : pas d’extras détaillés
    return null;
  }, [
    state,
    booking,
    selectedRoom,
    selectedFlight,
    roomCategories,
    selectedMealPlan,
    quotePrice,
  ]);

  // Handlers
  const onChangeAdult = (i, v) => setAdults((a) => a.map((x, idx) => (idx === i ? v : x)));
  const onChangeChild = (i, v) => setChildren((a) => a.map((x, idx) => (idx === i ? v : x)));
  const allTravelers = useMemo(() => [...adults, ...children, ...infants], [
    adults,
    children,
    infants,
  ]);

  const invalidTraveler = (t) => {
    if (!t?.firstName || !t?.lastName) return true;

    const d = t?.dob || {};
    const hasAnyDob = d.day || d.month || d.year;
    const hasFullDob = d.day && d.month && d.year;

    // ✅ si vide -> OK ; si partielle -> KO
    // ✅ adulte: dob obligatoire
    if ((t.kind || 'adult') === 'adult' && !hasFullDob) return true;

    // ✅ child/infant: si partielle => KO, si vide => OK
    if ((t.kind || '') !== 'adult' && hasAnyDob && !hasFullDob) return true;

    return false;
  };

  const expectedCount = numAdults + numChildren + numInfants;

  const canSubmit =
    !!bookingId &&
    allTravelers.length === expectedCount &&
    allTravelers.every((t) => !invalidTraveler(t)) &&
    !!contact?.email;

  async function startWorldlinePayment(payload = {}) {
    setPayError(null);
    const base = getPaymentsBaseUrl();

    try {
      const { data } = await axios.post(`${base}/worldline/${bookingId}/init`, payload, {
        headers: getAuthHeaders(),
        timeout: 15000,
      });

      if (!data?.ok || !data?.redirectUrl) {
        throw new Error(data?.message || 'Erreur lors de la création du paiement en ligne.');
      }

      window.location.href = data.redirectUrl;
    } catch (err) {
      console.error('WORLDLINE_INIT_FAILED', err?.response || err);
      setPayError(
        err?.response?.data?.message ||
          err?.response?.data?.detail ||
          err.message ||
          'Impossible de rediriger vers la page de paiement.',
      );
      throw err;
    }
  }

  function validateContact(c) {
    const e = {};

    if (!c.civility) e.civility = 'Merci de choisir une civilité.';
    if (!c.firstName.trim()) e.firstName = 'Le prénom est obligatoire.';
    if (!c.lastName.trim()) e.lastName = 'Le nom est obligatoire.';
    if (!c.phone.trim()) e.phone = 'Le téléphone est obligatoire.';

    if (!c.email.trim()) {
      e.email = 'L’email est obligatoire.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email)) {
      e.email = 'L’email n’est pas valide.';
    }

    if (!c.address.trim()) e.address = 'L’adresse est obligatoire.';
    if (!c.city.trim()) e.city = 'La ville est obligatoire.';
    if (!c.zip.trim()) e.zip = 'Le code postal est obligatoire.';
    if (!c.countryCode) e.countryCode = 'Le pays est obligatoire.';

    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const newContactErrors = validateContact(contact);
    setErrors(newContactErrors);

    const newTravelerErrors = validateTravelers({
      adults,
      children,
      infants,
      departDate: stayStartDateStr,
    });
    setTravelerErrors(newTravelerErrors);

    if (
      Object.keys(newContactErrors).length > 0 ||
      Object.keys(newTravelerErrors).length > 0
    ) {
      const el = document.querySelector('.is-invalid');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (!acceptCgv) {
      setCgvError('Vous devez accepter les conditions générales de vente.');
      return;
    }

    setCgvError('');

    if (!canSubmit) {
      alert('Formulaire incomplet.');
      return;
    }

    const hasFullDob = (d) => d?.year && d?.month && d?.day;

    const mapTraveler = (t, kind) => ({
      kind,
      civility: t?.civility || null,
      firstName: t?.firstName?.trim() || '',
      lastName: t?.lastName?.trim() || '',
      dob: hasFullDob(t?.dob) ? toISO(t.dob.year, t.dob.month, t.dob.day) : null,
    });

    const travelers = [
      ...adults.map((t) => mapTraveler(t, 'adult')),
      ...children.map((t) => mapTraveler(t, 'child')),
      ...infants.map((t) => mapTraveler(t, 'infant')),
    ];

    try {
      setSaving(true);
      setPayError(null);

      const token = localStorage.getItem('token_b2b');
      const guestToken = localStorage.getItem('booking_guest_token');
      const base = token ? '/api/bookings' : '/api/public/bookings';

      await axios.post(
        `${base}/${bookingId}/travelers`,
        { contact, travelers },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(!token && guestToken ? { 'X-Booking-Token': guestToken } : {}),
          },
        },
      );

      // ---------------- Paiement selon choix UI ----------------
      async function confirmAccountPayment() {
        const basePay = getPaymentsBaseUrl();
        const headers = getAuthHeaders();
        await axios.post(`${basePay}/account/${bookingId}/confirm`, {}, { headers });
      }

      // 1) En compte (B2B seulement + autorisé)
      if (selectedPayMode === 'account') {
        if (!canShowAccount) {
          // fallback sécurité
          setSelectedPayMode('card');
          await startWorldlinePayment({ cardOption: 'full' });
          return;
        }

        await confirmAccountPayment();
        history.push(`/b2b/confirmation/${bookingId}`);
        return;
      }

      // 2) Carte bancaire (B2B et B2C)
      await startWorldlinePayment({
        cardOption: selectedCardOption, // 'deposit40' | 'full'
      });
    } catch (err) {
      console.error('SAVE_TRAVELERS_OR_PAYMENT_FAILED', err);
      console.log('HTTP', err?.response?.status, err?.response?.data);

      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        'Échec de l’enregistrement des voyageurs ou du paiement.';
      alert(msg);
    } finally {
      setSaving(false);
    }
  }

  /* ------------------------------ UI ------------------------------ */
  if (loading) {
    return (
      <div className="container" style={{ marginTop: '7rem' }}>
        <PageLoader show label="Chargement de la réservation…" />
      </div>
    );
  }
  if (error) return <div className="container py-5 text-danger text-center">{error}</div>;
  if (!booking)
    return (
      <div className="container py-5 text-danger text-center">Réservation introuvable.</div>
    );
  if (!booking)
    return (
      <div className="container py-5 text-danger text-center">Réservation introuvable.</div>
    );

  console.log('%csrc\b2b.jsx:834 contact', 'color: #007acc;', contact);

  return (
    <div className="container" style={{ marginTop: '4rem' }}>
      {/* Loader superposé pendant l’enregistrement + init paiement */}
      <PageLoader show={saving} label="Traitement de votre demande (voyageurs + paiement)…" />

      <StepNav
        productId={state?.productId || booking?.productId}
        product={state?.product}
        formData={state?.formData}
        bookingId={bookingId}
      />

      {/* Erreur globale (ex : booking non trouvé) */}
      {error && (
        <div className="alert alert-danger mt-3" role="alert">
          {error}
        </div>
      )}

      {/* Erreur spécifique Worldline (startWorldlinePayment) */}
      {payError && (
        <div className="alert alert-danger mt-3" role="alert">
          {payError}
        </div>
      )}

      <div className="row g-4 mb-5">
        {/* Formulaire principal */}
        <div className="col-lg-8">
          <form onSubmit={handleSubmit}>
            <div className="card mb-4">
              <div className="card-header fw-semibold">Identification</div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-12 col-md-2">
                    <label className="form-label">Civilité</label>
                    <select
                      className={`form-control ${errors.civility ? 'is-invalid' : ''}`}
                      value={contact.civility}
                      onChange={(e) => {
                        setContact({ ...contact, civility: e.target.value });
                        setErrors({ ...errors, civility: undefined });
                      }}
                    >
                      <option value="">—</option>
                      <option value="Mr">M.</option>
                      <option value="Mme">Mme</option>
                      <option value="Mlle">Mlle</option>
                    </select>
                    {errors.civility && (
                      <div className="invalid-feedback">{errors.civility}</div>
                    )}
                  </div>

                  <div className="col-12 col-md-5">
                    <label className="form-label">Prénom</label>
                    <input
                      className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                      value={contact.firstName}
                      disabled={lockFields}
                      onChange={(e) => {
                        setContact({ ...contact, firstName: e.target.value });
                        setErrors({ ...errors, firstName: undefined });
                      }}
                    />
                    {errors.firstName && (
                      <div className="invalid-feedback">{errors.firstName}</div>
                    )}
                  </div>
                  <div className="col-12 col-md-5">
                    <label className="form-label">Nom</label>
                    <input
                      className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                      value={contact.lastName}
                      disabled={lockFields}
                      onChange={(e) => {
                        setContact({ ...contact, lastName: e.target.value });
                        setErrors({ ...errors, lastName: undefined });
                      }}
                    />
                    {errors.lastName && (
                      <div className="invalid-feedback">{errors.lastName}</div>
                    )}
                  </div>
                  {isB2B && (
                    <div className="col-12">
                      <label className="form-label">Agence</label>
                      <input
                        className="form-control"
                        value={contact.companyName || ''}
                        disabled={lockFields} // ✅ en B2B on l’affiche en lecture seule
                        // si tu veux autoriser édition:
                        // onChange={(e) => setContact({ ...contact, companyName: e.target.value })}
                      />
                    </div>
                  )}
                  <div className="col-12 col-md-6">
                    <label className="form-label">Téléphone</label>
                    <input
                      className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                      value={contact.phone}
                      onChange={(e) => {
                        setContact({ ...contact, phone: e.target.value });
                        setErrors({ ...errors, phone: undefined });
                      }}
                      disabled={lockFields}
                    />
                    {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Téléphone 2 (facultatif)</label>
                    <input
                      className="form-control"
                      value={contact.phone2}
                      onChange={(e) => setContact({ ...contact, phone2: e.target.value })}
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      value={contact.email}
                      onChange={(e) => {
                        setContact({ ...contact, email: e.target.value });
                        setErrors({ ...errors, email: undefined });
                      }}
                      disabled={lockFields}
                    />
                    {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                  </div>

                  <div className="col-12">
                    <label className="form-label">Adresse</label>
                    <input
                      className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                      value={contact.address}
                      onChange={(e) => {
                        setContact({ ...contact, address: e.target.value });
                        setErrors({ ...errors, address: undefined });
                      }}
                      disabled={lockFields}
                    />
                    {errors.address && (
                      <div className="invalid-feedback">{errors.address}</div>
                    )}
                  </div>

                  <div className="col-6 col-md-3">
                    <label className="form-label">Ville</label>
                    <input
                      className="form-control"
                      value={contact.city}
                      onChange={(e) => setContact({ ...contact, city: e.target.value })}
                      disabled={lockFields}
                    />
                  </div>
                  <div className="col-6 col-md-3">
                    <label className="form-label">Code postal</label>
                    <input
                      className="form-control"
                      value={contact.zip}
                      onChange={(e) => setContact({ ...contact, zip: e.target.value })}
                      disabled={lockFields}
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label">Pays</label>
                    <select
                      className="form-control"
                      value={contact.countryCode}
                      onChange={(e) => setContact({ ...contact, countryCode: e.target.value })}
                      disabled={lockFields}
                    >
                      {countryOptions.map(({ code, name }) => (
                        <option key={code} value={code}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <h5 className="mb-2">Voyageurs</h5>

            {adults.map((a, i) => (
              <TravelerCard
                key={`adult-${i}`}
                index={i}
                kind="adult"
                data={a}
                onChange={(v) => onChangeAdult(i, v)}
                errors={travelerErrors[`adult-${i}`] || {}}
              />
            ))}

            {children.map((c, i) => (
              <TravelerCard
                key={`child-${i}`}
                index={i}
                kind="child"
                data={c}
                onChange={(v) => onChangeChild(i, v)}
                prefilledDOB={booking?.childrenDOB?.[i]}
                errors={travelerErrors[`child-${i}`] || {}}
              />
            ))}

            {infants.map((t, i) => (
              <TravelerCard
                key={`infant-${i}`}
                index={i}
                kind="infant"
                data={t}
                prefilledDOB={bookingInfantsDOB[i]}
                errors={travelerErrors[`infant-${i}`] || {}}
                onChange={(next) =>
                  setInfants((prev) =>
                    prev.map((x, idx) => (idx === i ? { ...next, kind: 'infant' } : x)),
                  )
                }
              />
            ))}

            {/* -------------------- Choix paiement (après voyageurs) -------------------- */}
            <div className="card mb-3">
              <div className="card-header fw-semibold">Finaliser votre réservation</div>

              <div className="card-body">
                <div className="fw-semibold mb-2">Choisissez votre moyen de paiement</div>

                {/* CB - toujours */}
                <label className="border rounded p-3 d-flex align-items-center gap-3 w-100 mb-2">
                  <input
                    type="radio"
                    name="payMode"
                    checked={selectedPayMode === 'card'}
                    onChange={() => setSelectedPayMode('card')}
                  />
                  <i
                    class="fa fa-credit-card"
                    aria-hidden="true"
                    style={{ margin: '0px 15px 0px 15px', fontSize: '30px' }}
                  ></i>
                  <div className="flex-grow-1">
                    <div className="fw-semibold">Carte bancaire</div>
                    <div className="text-muted small">Visa, Apple Pay, Mastercard, ...</div>
                  </div>
                  <div>
                    <img src={visa} alt="Visa" style={{ height: 29, margin: '0 5px' }} />
                    <img
                      src={applePay}
                      alt="Apple Pay"
                      style={{ height: 50, margin: '0 5px' }}
                    />
                    <img
                      src={masterCard}
                      alt="Master Card"
                      style={{ height: 28, margin: '0 4px' }}
                    />
                    <img
                      src={googlePay}
                      alt="Google Pay"
                      style={{ height: 27, margin: '0 4px' }}
                    />
                    <img src={maestro} alt="Maestro" style={{ height: 33, margin: '0 5px' }} />
                    <img
                      src={samsungPay}
                      alt="Samsung Pay"
                      style={{ height: 30, margin: '0 4px' }}
                    />
                  </div>
                </label>

                {/* En compte - uniquement si autorisé et B2B */}
                {canShowAccount && (
                  <label className="border rounded p-3 d-flex align-items-center gap-3 w-100 mb-2">
                    <input
                      type="radio"
                      name="payMode"
                      checked={selectedPayMode === 'account'}
                      onChange={() => setSelectedPayMode('account')}
                    />
                    <i
                      class="fa fa-handshake"
                      style={{ margin: '0px 15px 0px 15px', fontSize: '30px' }}
                    ></i>
                    <div className="flex-grow-1">
                      <div className="fw-semibold">En compte</div>
                      <div className="text-muted small">
                        Validation directe, vous serez redirigé vers la confirmation.
                      </div>
                    </div>
                  </label>
                )}
              </div>
            </div>
            <div className="card mb-3">
              <div className="card-body">
                {/* Sous-options CB */}
                {selectedPayMode === 'card' && (
                  <div className="mt-3">
                    <div className="fw-semibold mb-2">Paiement par carte bancaire</div>

                    {/* 40% (si >=45 jours) */}
                    {canShowDeposit40 && (
                      <label className="border rounded p-3 d-flex align-items-center gap-3 w-100 mb-2">
                        <input
                          type="radio"
                          name="cardOption"
                          checked={selectedCardOption === 'deposit40'}
                          onChange={() => setSelectedCardOption('deposit40')}
                        />
                        <div className="flex-grow-1" style={{ margin: '0 15px' }}>
                          <div className="fw-semibold">Payer 40% maintenant</div>
                          <div className="text-muted small">
                            Disponible uniquement si le séjour commence dans 45 jours ou plus.
                          </div>
                        </div>
                      </label>
                    )}

                    {/* 100% */}
                    <label className="border rounded p-3 d-flex align-items-center gap-3 w-100">
                      <input
                        type="radio"
                        name="cardOption"
                        checked={selectedCardOption === 'full'}
                        onChange={() => setSelectedCardOption('full')}
                      />
                      <div className="flex-grow-1" style={{ margin: '0 15px' }}>
                        <div className="fw-semibold">Payer la totalité</div>
                        <div className="text-muted small">
                          Paiement immédiat du montant total.
                        </div>
                      </div>
                    </label>

                    {/* petite info si 40% indisponible */}
                    {!canShowDeposit40 && (
                      <div className="text-muted small mt-2">
                        L’option “40%” n’est pas disponible : séjour dans{' '}
                        {daysUntilStay ?? '—'} jours.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-body">
                <div className="form-check">
                  <input
                    className={`form-check-input ${cgvError ? 'is-invalid' : ''}`}
                    type="checkbox"
                    id="acceptCgv"
                    checked={acceptCgv}
                    onChange={(e) => {
                      setAcceptCgv(e.target.checked);
                      if (e.target.checked) setCgvError('');
                    }}
                  />
                  <label className="form-check-label" htmlFor="acceptCgv">
                    J’ai lu et j’accepte les{' '}
                    <a
                      href="/conditions-generales-de-vente"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      conditions générales de vente
                    </a>
                    .
                  </label>
                  {cgvError && <div className="invalid-feedback d-block">{cgvError}</div>}
                </div>
              </div>
            </div>

            {/* Texte d’info paiement + bouton */}
            <div className="mt-3 mb-2 text-muted small">
              Après validation, vous serez redirigé vers la page de paiement sécurisée de
              Worldline pour régler votre réservation.
            </div>

            <button
              type="submit"
              className="btn btn-primary w-100"
              disabled={!acceptCgv || saving}
            >
              {saving ? 'Traitement en cours…' : 'Valider les voyageurs & payer maintenant'}
            </button>
          </form>
        </div>

        {/* Résumé devis / image produit */}
        <div className="col-lg-4">
          <QuoteSummary
            product={quoteProduct}
            search={quoteSearch}
            price={quotePrice}
            extras={{
              ...quoteExtras,
              prestations: selectedPrestationsForSummary,
              prestationsTotal: prestationsTotalForSummary,
            }}
            selectedFlights={selectedFlight || booking?.selectedFlights || null}
            selectedRoom={selectedRoom || booking?.selectedRoom || null}
            discount={b2bDiscount}
          />
        </div>
      </div>
    </div>
  );
}
